import {NavigateFunction} from 'react-router-dom';
import {ICreateNewProtocolPayloadProps} from '../../../../../../../api/types/aiReview.type';
import {ProtocolFormData} from '../../../../../../../redux/slice/aiReviewSliceV2';

export const REVIEW_PROTOCOLS_TAB = 'reviewProtocols' as const;

export type ReviewProtocolsLocationState = {
  tab: typeof REVIEW_PROTOCOLS_TAB;
};

export const PROTOCOL_TAB_LANDING_PATH = '/AI/Review';

export const protocolTabLandingState = (): ReviewProtocolsLocationState => ({
  tab: REVIEW_PROTOCOLS_TAB,
});

export const navigateToProtocolTabLanding = (navigate: NavigateFunction) => {
  navigate(PROTOCOL_TAB_LANDING_PATH, {state: protocolTabLandingState()});
};

export const navigateToPendingOrProtocolTab = (
  navigate: NavigateFunction,
  pendingPath?: string | null
) => {
  if (pendingPath) {
    navigate(pendingPath);
    return;
  }
  navigateToProtocolTabLanding(navigate);
};

/**
 * Best-effort draft payload from V2 protocol form state.
 * API expects string caseContext/relevance (V1 shape) and optional parentProtocolID.
 * New create flows may not have a parent — 0 is used as a placeholder.
 */
export const buildCreateProtocolDraftPayload = (
  protocol: ProtocolFormData
): ICreateNewProtocolPayloadProps => {
  const issueCodes = protocol.issueCoding?.issue_codes
    ?.filter((code) => code.code_name || code.code_id || code.definition)
    .map((code) => ({
      code: code.code_name || code.code_id || '',
      question: code.definition || '',
      description: code.definition || '',
      id: code.code_id,
    }));

  return {
    parentProtocolID: protocol.protocolId ?? 0,
    filename: protocol.protocolName?.trim() || '',
    caseContext: JSON.stringify(protocol.caseContext ?? {}),
    relevance: JSON.stringify(protocol.relevanceCriteria ?? {}),
    ...(issueCodes?.length ? {issueCodes} : {}),
  };
};

const resolveInAppPathFromHref = (href: string | null): string | null => {
  if (!href || href.startsWith('#') || href.startsWith('javascript:')) {
    return null;
  }

  try {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) {
      return null;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
};

export const getInAppNavigationPath = (target: HTMLElement): string | null => {
  const navHrefEl = target.closest('[data-nav-href]') as HTMLElement | null;
  const dataNavHref = navHrefEl?.getAttribute('data-nav-href');
  if (dataNavHref) {
    return resolveInAppPathFromHref(dataNavHref);
  }

  const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
  return resolveInAppPathFromHref(anchor?.getAttribute('href') ?? null);
};

/** Clicks that must not open the "leave while processing" guard (global/system modals). */
export const LEAVE_GUARD_EXEMPT_SELECTOR = [
  '[data-testid="arr-leave-page-modal"]',
  '[data-testid="inactivity-modal-action"]',
  '.inactivity-modal-action',
  '.leave-page-modal',
].join(',');

export const isLeaveGuardExemptClick = (
  target: HTMLElement | null | undefined
): boolean => Boolean(target?.closest(LEAVE_GUARD_EXEMPT_SELECTOR));
